/**
 * callParticipant — pure resolution of the DISPLAY name/role for the global WebRTC call UI.
 *
 * The live bug ("User null"): for an AI-Voice → human HANDOFF the operator joins as ANSWERER and the
 * WebRTC `media_join` signaling model has NO staff participant — `currentCall.targetUser` is null.
 * The old UI rendered `User ${targetUser}` (via the `st.call.userFallback` = "User {id}" template),
 * which produced the literal "User null". A handoff's remote party is a CUSTOMER whose identity must
 * come from the ACCEPTED INTERACTION (customer_display_name, threaded onto `currentCall.customerName`
 * at accept time) — NEVER from the raw signaling/media_join model. media_join stays signaling-only.
 *
 * Fallback chain (handoff): currentCall.customerName (= customer_display_name → customer_name →
 * localized "Customer", resolved when the interaction is accepted) → localized "Customer". It NEVER
 * yields "User null", "User undefined", a raw firebase uid, a raw customer/participant id, or a
 * signaling session id.
 *
 * Extracted (like handoffMediaPhase) so it can be unit-tested without mounting the whole call tree.
 */

// TRUE when this active call is an AI-Voice human-handoff media leg (operator = answerer). Set by
// useWebRTC.joinMediaRoom; ordinary staff↔staff calls never carry role==='answerer'.
export function isHandoffCall(currentCall) {
  return !!currentCall && currentCall.role === 'answerer';
}

// A usable, non-id display string: a non-blank string that is not the literal "null"/"undefined".
function usableName(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s || s === 'null' || s === 'undefined') return null;
  return s;
}

/**
 * Resolve the display { name, role, isHandoff } for the ACTIVE call interface.
 * @param {object} args
 * @param {object|null} args.currentCall  the useWebRTC currentCall model
 * @param {function=}   args.resolveStaff (uid) => { name, role } | null  — staff roster resolver
 * @param {function=}   args.t            i18n translate fn (key, params) => string
 */
export function resolveCallParticipant({ currentCall, resolveStaff, t } = {}) {
  const tr = typeof t === 'function' ? t : (k) => k;
  if (!currentCall) return { name: '', role: '', isHandoff: false };

  // AI-Voice → human handoff: CUSTOMER identity from the accepted interaction ONLY.
  if (isHandoffCall(currentCall)) {
    const name = usableName(currentCall.customerName) || tr('od.opCallCustomer');
    return { name, role: '', isHandoff: true };
  }

  // Staff↔staff call: resolve the real staff member by firebase_uid.
  const staff = (typeof resolveStaff === 'function') ? resolveStaff(currentCall.targetUser) : null;
  const staffName = staff && usableName(staff.name);
  if (staffName) return { name: staffName, role: staff.role || '', isHandoff: false };

  // Unknown staff → labelled id ("User <uid>") ONLY when a real id exists; NEVER "User null"/"User
  // undefined". With no usable id we render nothing rather than a raw/placeholder id.
  const uid = currentCall.targetUser;
  const uidStr = (uid === null || uid === undefined) ? '' : String(uid).trim();
  const name = (uidStr && uidStr !== 'null' && uidStr !== 'undefined')
    ? tr('st.call.userFallback', { id: uidStr })
    : '';
  return { name, role: '', isHandoff: false };
}

/**
 * Resolve the display { name, role } for the incoming staff-call modal (IncomingCallModal). Same
 * null-safety: a real staff caller resolves to callerInfo.name → roster name → "User <uid>"; a
 * blank/absent caller falls back to the localized "Customer" instead of the literal "User null".
 */
export function resolveIncomingCallerName({ incomingCall, resolveStaff, t } = {}) {
  const tr = typeof t === 'function' ? t : (k) => k;
  if (!incomingCall) return { name: '', role: '' };
  const base = (incomingCall.caller || '').split('__')[0];
  const staff = (typeof resolveStaff === 'function') ? resolveStaff(base) : null;
  const name = usableName(incomingCall.callerInfo?.name)
    || (staff && usableName(staff.name))
    || (base ? tr('st.call.userFallback', { id: base }) : tr('od.opCallCustomer'));
  const role = incomingCall.callerInfo?.role || staff?.role || '';
  return { name, role };
}
